import { Fragment } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Search,
  Users,
  Footprints,
  HeartHandshake,
  ClipboardCheck,
  Eye,
  ShieldCheck,
} from "lucide-react";
import SEO from "../components/SEO";
import { Reveal, LineReveal } from "../components/Reveal";
import { images } from "../content/site";
import { getTeam, getSupporters, getPillar, getPillarOrder } from "../lib/content";

const TEAL = "#43D2FF";
const PINK = "#FF2F89";
const AMBER = "#FFC300";
const TEAL_TEXT = "#0B7A8C";
const PINK_TEXT = "#D6106A";
const AMBER_TEXT = "#8F6D00";

// A. The Gap → The Bridge → The Belief (progression tinted pink → amber → teal, echoing the logo)
const chapters = [
  {
    num: "01",
    title: "The gap",
    accent: PINK,
    accentText: PINK_TEXT,
    text: "Many people want to help — but don't know whom to trust, where help is genuinely needed, or how to participate without a huge commitment. So the intention to help quietly expires.",
  },
  {
    num: "02",
    title: "The bridge",
    accent: AMBER,
    accentText: AMBER_TEXT,
    text: "PilCoo exists to bridge that gap — finding genuine needs and connecting them with people ready to help, in ways that fit their lives.",
  },
  {
    num: "03",
    title: "The belief",
    accent: TEAL,
    accentText: TEAL_TEXT,
    text: "Because The PilCoo Foundation existed, no act of kindness was too small to matter. Small acts. Connected well. Meaningful change.",
  },
];

// D. How PilCoo works — the six-step open loop
const modelSteps = [
  {
    icon: Search,
    title: "Need identified",
    color: TEAL,
    textColor: TEAL_TEXT,
    text: "A genuine need is found and verified — by us, or flagged by someone in the community.",
  },
  {
    icon: Users,
    title: "Right people connected",
    color: PINK,
    textColor: PINK_TEXT,
    text: "The need is matched with the people or organisations best placed to meet it.",
  },
  {
    icon: Footprints,
    title: "Community participates",
    color: AMBER,
    textColor: AMBER_TEXT,
    text: "People join in — walking, sponsoring, planting, listening — in ways that fit their lives.",
  },
  {
    icon: HeartHandshake,
    title: "Action happens",
    color: TEAL,
    textColor: TEAL_TEXT,
    text: "The connected people and organisations do the work. PilCoo facilitates — it doesn't own every part of the solution.",
  },
  {
    icon: ClipboardCheck,
    title: "Outcome documented",
    color: PINK,
    textColor: PINK_TEXT,
    text: "What happened is recorded honestly: counts, photos, receipts of effort.",
  },
  {
    icon: Eye,
    title: "Impact shared transparently",
    color: AMBER,
    textColor: AMBER_TEXT,
    text: "The outcome is published openly, so the community can see the result of its care.",
  },
];

export default function About() {
  return (
    <main>
      <SEO
        title="About"
        description="Many people want to help but don't know where to start. PilCoo exists to bridge that gap. Small acts. Connected well. Meaningful change."
        path="/about"
      />

      {/* WHY PILCOO EXISTS */}
      <section className="pt-32 md:pt-44 pb-20 md:pb-28" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs uppercase tracking-[0.25em] font-medium text-ink-2">About PilCoo</p>
          <h1 className="mt-6 font-serif tracking-tighter leading-[0.98] text-ink text-5xl sm:text-6xl lg:text-7xl max-w-4xl">
            <LineReveal lines={["Most people want to help.", "Few know where to start."]} delay={0.15} />
          </h1>
          <Reveal delay={0.5}>
            <p className="mt-10 max-w-2xl text-base md:text-lg leading-relaxed text-ink-2">
              PilCoo is not a large institution and doesn't aspire to behave like
              one. It's a compassion-led initiative built on a simple observation:
              the willingness to help is everywhere — what's missing is the
              connection between that willingness and the places it can genuinely
              matter.
            </p>
          </Reveal>
        </div>
      </section>

      {/* THE GAP → THE BRIDGE → THE BELIEF */}
      <section className="border-t border-line py-20 md:py-28" data-testid="about-chapters">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] font-medium text-ink-2">Why PilCoo exists</p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-8">
            {chapters.map((c, i) => (
              <Reveal key={c.num} delay={i * 0.12} className="relative">
                <article
                  className="h-full border-t-2 pt-8 pr-2 lg:pr-8"
                  style={{ borderTopColor: c.accent }}
                  data-testid={`about-chapter-${c.num}`}
                >
                  <div className="flex items-baseline justify-between gap-4">
                    <span className="font-serif italic text-6xl md:text-7xl leading-none" style={{ color: c.accent }}>
                      {c.num}
                    </span>
                    {i < chapters.length - 1 && (
                      <ArrowRight className="hidden lg:block h-5 w-5 text-ink/25" aria-hidden="true" />
                    )}
                  </div>
                  <h2 className="mt-6 font-serif text-3xl tracking-tight" style={{ color: c.accentText }}>
                    {c.title}
                  </h2>
                  <p className="mt-4 text-sm md:text-base leading-relaxed text-ink-2 max-w-sm">{c.text}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MISSION | VISION — foundational statements on ink */}
      <section className="bg-ink text-sand py-20 md:py-32" data-testid="about-mission">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] font-medium text-sand/60">
              <span className="flex items-center gap-1.5" aria-hidden="true">
                <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: PINK }} />
                <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: AMBER }} />
                <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: TEAL }} />
              </span>
              Compassion in action, every day
            </p>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 lg:grid-cols-2">
            <Reveal>
              <div className="lg:pr-16">
                <p className="text-xs uppercase tracking-[0.2em] font-semibold text-sand/50">Our mission</p>
                <p className="mt-6 font-serif text-3xl md:text-4xl leading-snug tracking-tight text-sand">
                  To nurture compassion across all life — caring for animals,
                  protecting the natural world, and supporting mental wellbeing —
                  through small, consistent, everyday acts.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="mt-14 lg:mt-0 pt-14 lg:pt-0 lg:pl-16 border-t lg:border-t-0 lg:border-l border-sand/15">
                <p className="text-xs uppercase tracking-[0.2em] font-semibold text-sand/50">Our vision</p>
                <p className="mt-6 font-serif text-3xl md:text-4xl leading-snug tracking-tight text-sand">
                  A world where kindness is ordinary; where the wellbeing of every
                  being — human, animal, and the living earth — is held with equal
                  care.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PILKI + COOKIE — the quiet, personal moment */}
      <section className="py-20 md:py-28" data-testid="about-why-pilcoo">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <span className="inline-flex items-center gap-1.5" aria-hidden="true">
              <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: PINK }} />
              <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: AMBER }} />
              <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: TEAL }} />
            </span>
            <p className="mt-6 text-xs uppercase tracking-[0.25em] font-medium text-ink-2">Why “PilCoo”?</p>
            <h2 className="mt-5 font-serif text-3xl md:text-5xl tracking-tight text-ink leading-tight">
              PilCoo began with two names — <em className="text-ink-2">Pilki and Cookie.</em>
            </h2>
            <p className="mt-8 text-base md:text-lg leading-relaxed text-ink-2">
              Pilki, a Dalmatian, and Cookie, an indie, were family — the most
              consistent presence in our founders' lives. They asked for nothing
              and gave everything, and the love, loyalty and care they brought
              became part of a larger belief: that compassion deserves to be
              carried forward.
            </p>
            <p className="mt-5 text-base md:text-lg leading-relaxed text-ink-2">
              That belief lives today in how The PilCoo Foundation hopes to show
              up — for animals, for people, and for the world we share.
            </p>
            <p className="mt-8 font-serif italic text-xl md:text-2xl leading-snug text-ink">
              “Everything we build is, in the end, an attempt to be a little more like them.”
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-6 md:py-10" data-testid="about-image">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="overflow-hidden rounded-xl aspect-[21/9]">
              <img
                src={images.about}
                alt="People and animals crossing a misty green field at dawn in rural India"
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* HOW PILCOO WORKS — visual six-step process flow */}
      <section className="py-20 md:py-28 bg-sand-2 border-y border-line" data-testid="about-model">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.25em] font-medium text-ink-2">The model</p>
              <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight text-ink">
                How PilCoo works
              </h2>
              <p className="mt-6 text-base leading-relaxed text-ink-2">
                The PilCoo Foundation acts as a connector, facilitator and
                catalyst — not an organisation trying to own every part of the
                solution. Every initiative follows the same open loop, so you
                always know where your effort went.
              </p>
            </Reveal>
          </div>

          {/* Desktop: alternating connected horizontal journey */}
          <Reveal delay={0.1}>
            <div className="relative mt-20 hidden lg:block" data-testid="model-flow-desktop">
              <div aria-hidden="true" className="absolute left-0 right-0 top-1/2 h-px -translate-y-1/2 bg-ink/15" />
              <ArrowRight
                aria-hidden="true"
                className="absolute -right-1 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/30"
              />
              <ol className="relative grid grid-cols-6 gap-6">
                {modelSteps.map((step, i) => {
                  const above = i % 2 === 0;
                  return (
                    <li key={step.title} className="flex h-[21rem] flex-col">
                      {above ? (
                        <Fragment>
                          <div className="flex flex-1 flex-col justify-end pb-7" data-testid={`model-step-${i + 1}`}>
                            <step.icon className="h-5 w-5" style={{ color: step.textColor }} aria-hidden="true" />
                            <h3 className="mt-3 font-serif text-lg leading-snug tracking-tight text-ink">
                              {step.title}
                            </h3>
                            <p className="mt-2 text-xs leading-relaxed text-ink-2">{step.text}</p>
                          </div>
                          <div className="flex h-10 shrink-0 items-center">
                            <span
                              className="flex h-10 w-10 items-center justify-center rounded-full border-2 bg-sand-2 font-serif italic text-sm"
                              style={{ borderColor: step.color, color: step.textColor }}
                            >
                              {i + 1}
                            </span>
                          </div>
                          <div className="flex-1" />
                        </Fragment>
                      ) : (
                        <Fragment>
                          <div className="flex-1" />
                          <div className="flex h-10 shrink-0 items-center">
                            <span
                              className="flex h-10 w-10 items-center justify-center rounded-full border-2 bg-sand-2 font-serif italic text-sm"
                              style={{ borderColor: step.color, color: step.textColor }}
                            >
                              {i + 1}
                            </span>
                          </div>
                          <div className="flex flex-1 flex-col justify-start pt-7" data-testid={`model-step-${i + 1}`}>
                            <step.icon className="h-5 w-5" style={{ color: step.textColor }} aria-hidden="true" />
                            <h3 className="mt-3 font-serif text-lg leading-snug tracking-tight text-ink">
                              {step.title}
                            </h3>
                            <p className="mt-2 text-xs leading-relaxed text-ink-2">{step.text}</p>
                          </div>
                        </Fragment>
                      )}
                    </li>
                  );
                })}
              </ol>
            </div>
          </Reveal>

          {/* Mobile / tablet: vertical connected timeline */}
          <ol className="mt-14 lg:hidden" data-testid="model-flow-mobile">
            {modelSteps.map((step, i) => (
              <Reveal key={step.title} delay={i * 0.06}>
                <li className="relative flex gap-5 pb-10 last:pb-0" data-testid={`model-step-m-${i + 1}`}>
                  <div className="flex flex-col items-center">
                    <span
                      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 bg-sand-2 font-serif italic text-sm"
                      style={{ borderColor: step.color, color: step.textColor }}
                    >
                      {i + 1}
                    </span>
                    {i < modelSteps.length - 1 && (
                      <span className="mt-2 w-px grow bg-ink/15" aria-hidden="true" />
                    )}
                  </div>
                  <div className="pt-1">
                    <step.icon className="h-5 w-5" style={{ color: step.textColor }} aria-hidden="true" />
                    <h3 className="mt-3 font-serif text-2xl tracking-tight text-ink">{step.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-ink-2 max-w-md">{step.text}</p>
                  </div>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* NOW | NEXT — distinct states */}
      <section className="py-20 md:py-28" data-testid="about-roadmap">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] font-medium text-ink-2">Where we're going</p>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight text-ink max-w-3xl">
              Honest about today. <em className="text-ink-2">Ambitious about tomorrow.</em>
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Reveal>
              <div className="h-full rounded-xl bg-ink text-sand p-8 md:p-10" data-testid="about-now-panel">
                <p className="flex items-center gap-2 text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-sand/60">
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: TEAL }} />
                  Now — real, running work
                </p>
                <h3 className="mt-4 font-serif text-2xl md:text-3xl tracking-tight">What we're doing now</h3>
                <ul className="mt-7 space-y-5 text-sm md:text-base leading-relaxed text-sand/80">
                  <li className="flex gap-3">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: TEAL }} />
                    1 Lakh Steps for Paws — our free community walking challenge for animal-welfare awareness
                  </li>
                  <li className="flex gap-3">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: TEAL }} />
                    Building a community of people who want to help across animals, people and nature
                  </li>
                  <li className="flex gap-3">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: TEAL }} />
                    Creating stories and conversations that make helping feel possible
                  </li>
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="h-full border border-dashed border-ink/30 rounded-xl p-8 md:p-10" data-testid="about-next-panel">
                <p className="flex items-center gap-2 text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-ink-2">
                  <span className="h-2 w-2 rounded-full border border-ink/30" />
                  Next — building toward
                </p>
                <h3 className="mt-4 font-serif text-2xl md:text-3xl tracking-tight text-ink">What we're building toward</h3>
                <ul className="mt-7 space-y-5">
                  {getPillarOrder().map((key) => {
                    const p = getPillar(key);
                    return (
                      <li key={key}>
                        <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em]" style={{ color: p.accentText }}>
                          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: p.accent }} />
                          {p.name}
                        </p>
                        <p className="mt-1.5 text-sm text-ink-2 leading-relaxed">{p.buildingToward.join(" · ")}</p>
                      </li>
                    );
                  })}
                </ul>
                <p className="mt-7 text-xs leading-relaxed text-ink-2/80 border-t border-line pt-5">
                  These are aspirations, not running programmes — each will be
                  announced here when it becomes real.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CORE TEAM — restrained editorial tiles, no photographs */}
      <section className="py-20 md:py-28 border-t border-line" data-testid="about-team">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] font-medium text-ink-2">The people behind PilCoo</p>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight text-ink">
              A small team, <em className="text-ink-2">on purpose.</em>
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {getTeam().map((member, i) => (
              <Reveal key={member.id} delay={i * 0.06}>
                <div
                  className="h-full border border-line rounded-xl bg-sand p-7 md:p-8"
                  data-testid={`team-member-${member.id}`}
                >
                  <span className="font-serif italic text-lg text-ink/25">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-4 font-serif text-2xl tracking-tight text-ink">{member.name}</h3>
                  <p className="mt-3 border-t border-line pt-3 text-[0.68rem] uppercase tracking-[0.18em] text-ink-2">
                    {member.role}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FOUNDING SUPPORT */}
      <section className="py-20 md:py-28 bg-sand-2 border-y border-line" data-testid="about-founding-support">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] font-medium text-ink-2">Founding Support</p>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight text-ink max-w-2xl">
              Backed from the beginning.
            </h2>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink-2">
              The PilCoo Foundation is being built with early support from
              businesses that share the belief that commercial success can
              create room for meaningful social impact.
            </p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {getSupporters().map((s, i) => (
              <Reveal key={s.id} delay={i * 0.08}>
                <div
                  className="h-full rounded-xl border border-line border-t-2 border-t-ink bg-sand p-8"
                  data-testid={`supporter-${s.id}`}
                >
                  <span className="font-serif italic text-lg text-ink/25">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="mt-4 font-serif text-2xl md:text-3xl tracking-tight text-ink">{s.name}</p>
                  <p className="mt-2 text-[0.65rem] uppercase tracking-[0.2em] text-ink-2">{s.category}</p>
                  <p className="mt-4 text-sm leading-relaxed text-ink-2">{s.note}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT WE DON'T CLAIM — integrity statement + closing CTA */}
      <section className="py-20 md:py-28" data-testid="about-honesty">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="rounded-xl border border-line border-l-4 border-l-ink bg-sand-2 p-8 md:p-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
              <div className="lg:col-span-4">
                <ShieldCheck className="h-7 w-7 text-ink" aria-hidden="true" />
                <h2 className="mt-5 font-serif text-3xl md:text-4xl tracking-tight text-ink">
                  What we don't claim
                </h2>
              </div>
              <div className="lg:col-span-8">
                <p className="text-base leading-relaxed text-ink-2">
                  In the spirit of transparency: PilCoo does not currently claim
                  NGO registration, charitable status, tax benefits, legal
                  certifications, formal partnerships or fundraising permissions.
                  As formal milestones are reached, they will be published on our{" "}
                  <Link to="/transparency" className="underline underline-offset-4 text-ink">
                    transparency page
                  </Link>
                  . Until then, we'd rather earn your trust slowly than borrow it.
                </p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="mt-16 text-center">
              <p className="font-serif text-2xl md:text-3xl tracking-tight text-ink max-w-xl mx-auto">
                The next chapter of PilCoo is written by the people who join it.
              </p>
              <Link
                to="/get-involved"
                data-testid="about-cta"
                className="group mt-8 inline-flex items-center gap-2 rounded-full bg-action text-sand px-7 py-3.5 text-sm font-semibold transition-transform duration-300 hover:scale-[1.04] active:scale-95"
              >
                Help us write the next chapter
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
